import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Collapse,
  Snackbar,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import WarningAmberIcon from '@material-ui/icons/Warning';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { formatCnpj } from '@brazilian-utils/formatters';
import { useQuery } from 'react-query';
import partnershipRequest from '../../../../services/network/partnershipRequest';
import { useAuth } from '../../../../auth/authProvider';
import palette from '../../../../theme/palette';
import { useStyles } from './PartnerDocumentsModal.styles';

const BANK_DOCUMENT_TYPES = ['COMPROVANTE_BANCARIO'];

const DOCUMENT_TYPE_LABELS = {
  CARTAO_CNPJ: 'Cartão CNPJ',
  COMPROVANTE_BANCARIO: 'Comprovante Bancário',
  DOCUMENTO_SOCIAL: 'Contrato Social ou Estatuto Social',
  RG_FRENTE: 'RG ou CNH (Frente)',
  RG_VERSO: 'RG ou CNH (Verso)',
  COMPROVANTE_RESIDENCIA: 'Comprovante de Residência',
  CPF: 'CPF',
  NOTA_FISCAL: 'Nota Fiscal',
};

const STATUS_CONFIG = {
  AGUARDANDO_VALIDACAO: {
    label: 'Aguardando',
    color: '#1565C0',
    Icon: AccessTimeIcon,
  },
  APROVADA: {
    label: 'Aprovado',
    color: palette.success?.dark || '#2E7D32',
    Icon: CheckCircleOutlineIcon,
  },
  RECUSADA: {
    label: 'Reprovado',
    color: palette.danger?.main || '#D32F2F',
    Icon: CancelOutlinedIcon,
  },
  COM_PENDENCIA: {
    label: 'Pendente',
    color: palette.secondary?.main || '#E65100',
    Icon: WarningAmberIcon,
  },
  VALIDACAO_INTERNA: {
    label: 'Validação Interna',
    color: '#4527A0',
    Icon: AccessTimeIcon,
  },
};

const STATUS_ACTION_LABEL = {
  APROVADA: 'Documento aprovado',
  RECUSADA: 'Documento reprovado',
  COM_PENDENCIA: 'Pendência identificada',
  AGUARDANDO_VALIDACAO: 'Em análise',
  VALIDACAO_INTERNA: 'Validado internamente',
};

/** API partnership: `hasFile`; ausente em clientes antigos = assume que há arquivo. */
function registrationDocumentHasFile(doc) {
  return doc.hasFile !== false;
}

const REASON_LABELS = {
  DADOS_ILEGIVEIS: 'Dados ilegíveis — imagem com baixa qualidade, desfocada ou cortada',
  DOCUMENTO_VENCIDO: 'Documento vencido ou expirado',
  DOCUMENTO_INVALIDO: 'Documento inválido ou não aceito para este cadastro',
  TIPO_DOCUMENTO_INCORRETO: 'Tipo de documento incorreto — não é o solicitado',
  NOME_TITULAR_DIVERGENTE: 'Nome do titular diverge do cadastro',
  DOCUMENTO_TERCEIROS: 'Documento pertence a terceiros',
  DADOS_BANCARIOS_DIVERGENTES: 'Dados bancários não conferem com o titular',
  CONTA_BANCARIA_INVALIDA: 'Conta bancária inválida ou encerrada',
  COMPROVANTE_ENDERECO_DESATUALIZADO: 'Comprovante de endereço desatualizado — emissão superior a 90 dias',
  CNPJ_INATIVO: 'CNPJ inativo ou não localizado na Receita Federal',
  SELFIE_QUALIDADE_INSUFICIENTE: 'Selfie ou foto de identificação com qualidade insuficiente',
};

function buildHistoryEvents(doc) {
  const events = [];

  if (doc.createdAt) {
    events.push({
      date: new Date(doc.createdAt).toLocaleDateString('pt-BR'),
      action: 'Documento enviado',
      actor: 'Parceiro',
      reason: null,
    });
  }

  if (doc.status && doc.status !== 'AGUARDANDO_VALIDACAO') {
    events.push({
      date: new Date(doc.updatedAt).toLocaleDateString('pt-BR'),
      action: STATUS_ACTION_LABEL[doc.status] || doc.status,
      actor: doc.reviewedBy ? `PSM - ${doc.reviewedBy}` : 'Sistema',
      reason: doc.reason || null,
    });
  }

  return events;
}

function DocCard({ doc, classes, onDownload, onView }) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const canOpenFile = registrationDocumentHasFile(doc);
  const config = STATUS_CONFIG[doc.status] || STATUS_CONFIG.AGUARDANDO_VALIDACAO;
  const { label, color, Icon } = config;
  const sentDate = doc.createdAt
    ? `Enviado em ${new Date(doc.createdAt).toLocaleDateString('pt-BR')}`
    : 'Aguardando envio';

  const historyEvents = buildHistoryEvents(doc);

  const handleDownload = async () => {
    if (!canOpenFile || downloading) {
      return;
    }
    setDownloading(true);
    try {
      await onDownload(doc);
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    if (!canOpenFile || viewing) {
      return;
    }
    setViewing(true);
    try {
      await onView(doc);
    } finally {
      setViewing(false);
    }
  };

  return (
    <Box className={classes.docCard}>
      <Box className={classes.docCardRow}>
        <Box className={classes.docIconBox} style={{ backgroundColor: `${color}18` }}>
          <DescriptionOutlinedIcon style={{ fontSize: 18, color }} />
        </Box>

        <Box className={classes.docInfo}>
          <Typography className={classes.docName}>
            {DOCUMENT_TYPE_LABELS[doc.typeDocument] || doc.typeDocument}
          </Typography>
          <Typography className={classes.docDate}>{sentDate}</Typography>
        </Box>

        <Box className={classes.docActions}>
          {doc.id && (
            <>
              <IconButton
                size="small"
                className={classes.downloadBtn}
                onClick={handleView}
                disabled={!canOpenFile || viewing}
                title={canOpenFile ? 'Visualizar' : 'Sem arquivo no storage (ex.: aprovação automática Oracle)'}
              >
                {viewing ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon style={{ fontSize: 18 }} />}
              </IconButton>
              <IconButton
                size="small"
                className={classes.downloadBtn}
                onClick={handleDownload}
                disabled={!canOpenFile || downloading}
                title={canOpenFile ? 'Baixar' : 'Sem arquivo no storage (ex.: aprovação automática Oracle)'}
              >
                {downloading ? <CircularProgress size={16} /> : <GetAppIcon style={{ fontSize: 18 }} />}
              </IconButton>
            </>
          )}

          <span className={classes.statusLabel} style={{ color }}>
            <Icon style={{ fontSize: 15 }} />
            {label}
          </span>

          <IconButton size="small" className={classes.chevronBtn} onClick={() => setExpanded(prev => !prev)}>
            {expanded ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <ExpandMoreIcon style={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box className={classes.historyBox}>
          <Typography className={classes.historyTitle}>Histórico</Typography>
          {historyEvents.map((event, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={idx} className={classes.historyEvent}>
              <Box className={classes.historyDot} />
              <Box className={classes.historyEventContent}>
                <Box className={classes.historyEventRow}>
                  <Box>
                    <Typography className={classes.historyEventDate}>{event.date}</Typography>
                    <Typography className={classes.historyEventAction}>{event.action}</Typography>
                  </Box>
                  <Typography className={classes.historyEventActor}>{event.actor}</Typography>
                </Box>
                {event.reason && (
                  <Typography className={classes.historyReason}>
                    Motivo: {REASON_LABELS[event.reason] || event.reason}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          {historyEvents.length === 0 && (
            <Typography className={classes.historyEventDate}>Nenhum evento registrado.</Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

DocCard.propTypes = {
  doc: PropTypes.shape({
    id: PropTypes.string,
    typeDocument: PropTypes.string,
    status: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    name: PropTypes.string,
    reason: PropTypes.string,
    reviewedBy: PropTypes.string,
    hasFile: PropTypes.bool,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  onDownload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

function MissingDocCard({ docType, classes }) {
  const { color, Icon } = STATUS_CONFIG.COM_PENDENCIA;

  return (
    <Box className={classes.missingDocCard}>
      <Box className={classes.docIconBox}>
        <DescriptionOutlinedIcon style={{ color: 'rgba(0,0,0,0.4)', fontSize: 22 }} />
      </Box>
      <Box className={classes.docInfo}>
        <Typography className={classes.missingDocLabel}>{DOCUMENT_TYPE_LABELS[docType] || docType}</Typography>
        <Typography className={classes.missingDocSubLabel}>Aguardando envio</Typography>
      </Box>
      <Box className={classes.docActions}>
        <span className={classes.statusLabel} style={{ color }}>
          <Icon style={{ fontSize: 14 }} />
          Pendente
        </span>
      </Box>
    </Box>
  );
}

MissingDocCard.propTypes = {
  docType: PropTypes.string.isRequired,
  classes: PropTypes.shape({}).isRequired,
};

function DocumentList({ docs, missingDocTypes, classes, onDownload, onView }) {
  const hasContent = docs.length > 0 || missingDocTypes.length > 0;

  if (!hasContent) {
    return <Typography className={classes.emptyState}>Nenhum documento encontrado.</Typography>;
  }

  return (
    <Box className={classes.cardList}>
      {docs.map(doc => (
        <DocCard key={doc.id} doc={doc} classes={classes} onDownload={onDownload} onView={onView} />
      ))}
      {missingDocTypes.length > 0 && (
        <Box className={classes.missingSection}>
          {/* <Typography className={classes.missingSectionTitle}>Aguardando envio</Typography> */}
          <Box className={classes.cardList}>
            {missingDocTypes.map(type => (
              <MissingDocCard key={type} docType={type} classes={classes} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

DocumentList.propTypes = {
  docs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  missingDocTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  classes: PropTypes.shape({}).isRequired,
  onDownload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

function SummaryRow({ docs, missingCount, classes }) {
  const total = docs.length;
  const aprovados = docs.filter(d => d.status === 'APROVADA').length;
  const aguardando = docs.filter(d => d.status === 'AGUARDANDO_VALIDACAO').length;
  const reprovados = docs.filter(d => d.status === 'RECUSADA').length;
  const validacaoInterna = docs.filter(d => d.status === 'VALIDACAO_INTERNA').length;
  const greenColor = STATUS_CONFIG.APROVADA.color;
  const blueColor = STATUS_CONFIG.AGUARDANDO_VALIDACAO.color;
  const redColor = STATUS_CONFIG.RECUSADA.color;
  const purpleColor = STATUS_CONFIG.VALIDACAO_INTERNA.color;

  return (
    <Box className={classes.summaryRow}>
      <Typography className={classes.summaryText}>
        {total} {total === 1 ? 'documento' : 'documentos'}
      </Typography>
      {aprovados > 0 && (
        <Typography className={classes.summaryText} style={{ color: greenColor, fontWeight: 500 }}>
          {aprovados} {aprovados === 1 ? 'aprovado' : 'aprovados'}
        </Typography>
      )}
      {aguardando > 0 && (
        <Typography className={classes.summaryText} style={{ color: blueColor, fontWeight: 500 }}>
          {aguardando} aguardando
        </Typography>
      )}
      {validacaoInterna > 0 && (
        <Typography className={classes.summaryText} style={{ color: purpleColor, fontWeight: 500 }}>
          {validacaoInterna} {validacaoInterna === 1 ? 'validação interna' : 'validação interna'}
        </Typography>
      )}
      {reprovados > 0 && (
        <Typography className={classes.summaryText} style={{ color: redColor, fontWeight: 500 }}>
          {reprovados} {reprovados === 1 ? 'reprovado' : 'reprovados'}
        </Typography>
      )}
      {missingCount > 0 && (
        <Typography className={classes.summaryText} style={{ color: 'rgba(0,0,0,0.4)', fontWeight: 500 }}>
          {missingCount} {missingCount === 1 ? 'faltando' : 'faltando'}
        </Typography>
      )}
    </Box>
  );
}

SummaryRow.propTypes = {
  docs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  missingCount: PropTypes.number.isRequired,
  classes: PropTypes.shape({}).isRequired,
};

export function PartnerDocumentsModal({ open, partner, onClose }) {
  const classes = useStyles();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    setDownloadError(null);
  }, [open, partner?.partnerId]);

  function handleClose() {
    setDownloadError(null);
    onClose();
  }

  const handleDownload = async doc => {
    if (!doc?.id) {
      return;
    }
    try {
      const blob = await partnershipRequest.get(`partners/partner/${partner.partnerId}/documents/${doc.id}`, {
        token,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || `${doc.typeDocument}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Não foi possível baixar o arquivo. O documento pode não estar disponível no storage.');
    }
  };

  const handleView = async doc => {
    if (!doc?.id) {
      return;
    }
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      setDownloadError('Permita a abertura de pop-ups para visualizar o documento.');
      return;
    }
    previewWindow.document.write('Carregando documento...');

    try {
      const blob = await partnershipRequest.get(`partners/partner/${partner.partnerId}/documents/${doc.id}`, {
        token,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(blob);
      previewWindow.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      previewWindow.close();
      setDownloadError('Não foi possível carregar o arquivo para visualização.');
    }
  };

  const { data: documentsResponse, isFetching, isError } = useQuery(
    ['partner-documents', partner?.partnerId],
    () => partnershipRequest.get(`partners/partner/${partner.partnerId}/documents`, { token }),
    {
      enabled: open && !!partner?.partnerId,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const rawDocuments = documentsResponse?.documentos ?? [];
  const tiposObrigatorios = documentsResponse?.tiposObrigatorios ?? [];

  const documents = rawDocuments.reduce((acc, doc) => {
    const existing = acc.find(d => d.typeDocument === doc.typeDocument);
    if (!existing || new Date(doc.createdAt) > new Date(existing.createdAt)) {
      return [...acc.filter(d => d.typeDocument !== doc.typeDocument), doc];
    }
    return acc;
  }, []);

  const registrationDocs = documents.filter(d => !BANK_DOCUMENT_TYPES.includes(d.typeDocument));
  const bankDocs = documents.filter(d => BANK_DOCUMENT_TYPES.includes(d.typeDocument));
  const activeDocs = activeTab === 0 ? registrationDocs : bankDocs;

  const submittedTypes = documents.map(d => d.typeDocument);
  const allMissingTypes = tiposObrigatorios.filter(type => !submittedTypes.includes(type));
  const missingRegistrationTypes = allMissingTypes.filter(type => !BANK_DOCUMENT_TYPES.includes(type));
  const missingBankTypes = allMissingTypes.filter(type => BANK_DOCUMENT_TYPES.includes(type));
  const activeMissingTypes = activeTab === 0 ? missingRegistrationTypes : missingBankTypes;

  if (!partner) {
    return null;
  }

  const isLoading = isFetching && !documentsResponse;

  const docsTabLabel = (
    <>
      <DescriptionOutlinedIcon className={classes.tabIcon} />
      Documentos
      {missingRegistrationTypes.length > 0 && (
        <span className={classes.tabBadge}>{missingRegistrationTypes.length}</span>
      )}
    </>
  );

  const bankTabLabel = (
    <>
      <AccountBalanceOutlinedIcon className={classes.tabIcon} />
      Dados Bancários
      {missingBankTypes.length > 0 && <span className={classes.tabBadge}>{missingBankTypes.length}</span>}
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box pr={4}>
          <Typography className={classes.titleLabel}>Detalhes do Parceiro</Typography>
          <Typography className={classes.partnerName}>{partner.partnerName}</Typography>
          <Typography className={classes.cnpj}>{partner.cnpj ? formatCnpj(partner.cnpj) : '-'}</Typography>
        </Box>
        <IconButton className={classes.closeBtn} onClick={handleClose} size="small">
          <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
        </IconButton>
      </DialogTitle>

      <Divider />

      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab className={classes.tab} classes={{ wrapper: classes.tabWrapper }} label={docsTabLabel} />
        <Tab className={classes.tab} classes={{ wrapper: classes.tabWrapper }} label={bankTabLabel} />
      </Tabs>

      <Divider />

      <DialogContent className={classes.dialogContent}>
        {isLoading && (
          <Box className={classes.loadingWrapper}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && !isLoading && (
          <Typography className={classes.emptyState}>Erro ao carregar documentos. Tente novamente.</Typography>
        )}

        {!isLoading && !isError && (
          <>
            <SummaryRow docs={activeDocs} missingCount={activeMissingTypes.length} classes={classes} />
            <DocumentList
              docs={activeDocs}
              missingDocTypes={activeMissingTypes}
              classes={classes}
              onDownload={handleDownload}
              onView={handleView}
            />
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={handleClose} color="primary" variant="contained" disableElevation>
          Fechar
        </Button>
      </DialogActions>

      <Snackbar
        open={!!downloadError}
        autoHideDuration={5000}
        onClose={() => setDownloadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setDownloadError(null)} severity="error" elevation={6} variant="filled">
          {downloadError}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
}

PartnerDocumentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  partner: PropTypes.shape({
    partnerId: PropTypes.string,
    partnerName: PropTypes.string,
    cnpj: PropTypes.string,
    businessType: PropTypes.string,
    businessCategory: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

PartnerDocumentsModal.defaultProps = {
  partner: null,
};
